// libs
import { styled } from '@nykaa/ui-components';
import { css } from '@emotion/core';
import { hexToRgb } from '@nykaa/ui-components/styles/utils';

// components
import BaseOverlaySheet from 'shared/components/OverlaySheet';
import BaseImage from 'shared/components/ImageV2';
import BaseButton from 'shared/components/Button';
import UIButton from '@nykaa/ui-components/Button';
import { Spinner } from '@nykaa/ui-components/Loader';
import CouponErrorPageBase from 'shared/layouts/Coupons/CouponErrorPage';

//icons
import CircleLock from '@nykaa/ui-components/Icons/circle-lock';

export const OverlaySheet = styled(BaseOverlaySheet)`
  width: 100%;
  top: initial;
  animation-duration: 0.3s;
  ${({ theme }) => `background-color: ${theme.colors.surface}`};
  ${({ theme }) => `border-radius: ${theme.borders.radius40} ${theme.borders.radius40} 0 0}`};
`;

export const ViewDetailsContainer = styled.div`
  position: relative;
  padding-bottom: env(safe-area-inset-bottom);
  overflow-y: scroll;
  max-height: 90vh;
  height: 100%;
  display: flex;
  flex-direction: column;
  ${({ theme }) => `border-radius: ${theme.borders.radius40} ${theme.borders.radius40} 0 0}`};
`;

export const Header = styled.div<{
  showBottomBorder?: boolean;
  backgroundOpacity?: number;
  isHidden?: boolean;
}>`
  display: flex;
  height: 56px;

  ${({ theme, showBottomBorder }) =>
    showBottomBorder && `border-bottom: 1px solid ${theme.colors.textOutline};`};
  &.hasBackground {
    height: 136px;
    background-image: url('https://images-static.nykaa.com/nykdesignstudio-images/pub/media/coupons-applied.svg');
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
    ${({ theme }) => `border-radius: ${theme.borders.radius40} ${theme.borders.radius40} 0 0}`};
    opacity: ${({ backgroundOpacity }) => backgroundOpacity};
    ${({ isHidden }) => isHidden && 'display:none'};
  }
`;
export const HeaderWrapper = styled.div<{ showBottomBorder?: boolean; isHidden?: boolean }>`
  align-items: start;
  justify-content: start;
  display: ${({ isHidden }) => isHidden && 'none'};
  ${({ theme }) => `border-radius: ${theme.borders.radius40} ${theme.borders.radius40} 0 0}`};
  ${({ theme }) => `background-color: ${theme.colors.surface}`};
  ${({ theme, showBottomBorder }) =>
    showBottomBorder && `border-bottom: 1px solid ${theme.colors.textOutline}`};
  top: 0;
  &.sticky {
    position: sticky;
  }
`;

export const CloseIcon = styled.div`
  height: 24px;
  width: 24px;
  ${({ theme }) => `
    margin: ${theme.spacing.spacing80} ${theme.spacing.spacing80} ${theme.spacing.spacing80}
      ${theme.spacing.spacing100};
  `};
  z-index: 6;
  position: absolute;
  left: 0;
  &.close-icon {
    position: fixed;
  }
`;

export const HeaderTitle = styled.div`
  text-align: center;
  ${({ theme }) => theme.typography.titleMedium};
  color: ${({ theme }) => theme.colors.textPrimary};
  max-width: 100%;
  ${({ theme }) => `padding: ${theme.spacing.spacing50} 0 ${theme.spacing.spacing80}`};
`;

export const CartHeaderTitle = styled(HeaderTitle)<{ smallFont: boolean; isHidden: boolean }>`
  ${({ theme }) => `margin: 0 ${theme.spacing.spacing100}`};
  border-bottom: ${({ theme }) => `1px solid ${theme.colors.textOutline};`};
  position: sticky;
  top: 0;
  z-index: 5;
  ${({ theme }) => `background-color: ${theme.colors.surface};`};
  ${({ smallFont, theme }) => smallFont && theme.typography.titleSmall};
  ${({ theme }) => `padding: ${theme.spacing.spacing80} 0 ${theme.spacing.spacing80}`};
  display: ${({ isHidden }) => isHidden && 'none'};
  transition: font-size 0.3s;
`;

export const HighlightedText = styled.span`
  color: ${({ theme }) => theme.colors.positive};
`;

export const InfoContainer = styled.div<{ showBottomBorder?: boolean }>`
  display: flex;
  border-bottom: none;
  flex: 1;
  ${({ theme, showBottomBorder }) =>
    `
      margin: 0 ${theme.spacing.spacing100};
      padding: ${theme.spacing.spacing80} 0;
      ${showBottomBorder && `border-bottom: 1px solid ${theme.colors.textOutline};`};
    `}
`;

export const IconContainer = styled.div`
  height: 64px;
  max-width: 64px;
  min-width: 64px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: ${({ theme }) => theme.spacing.spacing80};
`;

export const Image = styled(BaseImage)<{ maxWidth: number; height: number }>`
  object-fit: contain;
  width: 64px;
  height: auto;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

export const CouponInfo = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

export const CouponInfoDesktop = styled(CouponInfo)`
  max-width: calc(100% - 80px);
`;

export const Title = styled.div<{ textCenter?: boolean }>`
  ${({ theme }) => theme.typography.titleSmall};
  color: ${({ theme }) => theme.colors.textPrimary};
  max-width: calc(100vw - 120px);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  ${({ textCenter, theme }) =>
    textCenter &&
    css`
      text-align: center;
      word-wrap: break-word;
      padding: 0 ${theme.spacing.spacing60} ${theme.spacing.spacing10} ${theme.spacing.spacing60};
    `};
`;

export const OfferTitle = styled(Title)`
  width: 100%;
  max-width: 100%;
`;

export const Subtitle = styled.div<{ textCenter?: boolean }>`
  max-height: 60px;
  overflow: hidden;
  ${({ theme, textCenter }) =>
    css`
      ${theme.typography.bodyMedium};
      color: ${theme.colors.primary};
      ${textCenter &&
      `text-align: center;
      word-wrap: break-word;
      padding: 0 ${theme.spacing.spacing60} ${theme.spacing.spacing10} ${theme.spacing.spacing60};`};
    `};
`;

export const TermsAndContitions = styled.div`
  ${({ theme }) => `padding: ${theme.spacing.spacing80} ${theme.spacing.spacing100}`};
`;

export const TermsAndContitionsDesktop = styled.div`
  ${({ theme }) => `padding: ${theme.spacing.spacing100} ${theme.spacing.spacing160}`};
  max-height: calc(100vh - 130px);
`;

export const TACHeading = styled.div`
  ${({ theme }) => theme.typography.buttonSmall};
  ${({ theme }) => `color: ${theme.colors.textSecondary}`};
`;
export const TACList = styled.ul<{ showLastBottomMargin: boolean }>`
  ${({ theme }) => css`
    margin: ${theme.spacing.spacing40} 0 0 0;
    padding: 0 0 0 ${theme.spacing.spacing60};
    color: ${theme.colors.textSecondary};
  `};

  & > li:last-of-type {
    ${({ showLastBottomMargin }) => !showLastBottomMargin && `margin-bottom: 0`};
  }
`;

export const TACItem = styled.li`
  ${({ theme }) => theme.typography.bodySmall};
  ${({ theme }) => `margin-bottom: ${theme.spacing.spacing40}`};
`;

export const ViewMoreButton = styled(BaseButton)<{ viewAll: boolean }>`
  ${({ theme }) => css`
    ${theme.typography.subTitleSmall};
    color: ${theme.colors.secondary};
  `};
  background-color: transparent;
  border: none;
  height: 16px;
  justify-content: flex-start;
  align-items: center;
  padding: 0;

  .chevronIcon {
    transform: rotate(90deg);
    height: 16px;
    width: 16px;
    transition: transform 0.3s linear;
    ${({ theme, viewAll }) =>
      css`
        margin: 0 0 0 ${theme.spacing.spacing20};
        ${viewAll && `transform: rotate(-90deg);`}
      `};
  }

  &:not([disabled]):hover {
    background: transparent;
  }
`;

export const BottomContainer = styled.div<{ showTopBorder: boolean; isDesktop?: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  bottom: 0;
  position: sticky;
  ${({ theme, showTopBorder, isDesktop }) =>
    css`
      background-color: ${theme.colors.surface};
      ${showTopBorder && `border-top: 1.12px solid ${theme.colors.textOutline}`};
      ${isDesktop && `position: absolute;`};
      max-height: 130px;
    `};
`;

export const CouponCodeContainer = styled.div<{ isApplied: boolean; bgColor: string }>`
  display: flex;
  width: 100%;
  flex-direction: row;

  ${({ theme, isApplied, bgColor }) =>
    css`
      background-color: ${bgColor};
      ${isApplied && `background-color: ${theme.colors.positive10}`};
      padding: ${theme.spacing.spacing20} ${theme.spacing.spacing100};
    `};
`;

export const AppliedText = styled.div`
  ${({ theme }) =>
    css`
      padding: ${theme.spacing.spacing20} ${theme.spacing.spacing40};
      ${theme.typography.labelSmall};
      color: ${theme.colors.surface};
      background-color: ${theme.colors.positive};
      border-radius: ${theme.borders.radius10};
    `};
`;
export const CouponCode = styled.div`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  ${({ theme }) => theme.typography.subTitleMedium}
  letter-spacing: 0.3px;
  margin: 0;
`;

export const ActionContainer = styled.div`
  ${({ theme }) => `padding: ${theme.spacing.spacing80} ${theme.spacing.spacing100}`};
  display: flex;
  justify-content: space-between;
`;

export const ActionContainerPDP = styled.div`
  ${({ theme }) => `padding: ${theme.spacing.spacing120} ${theme.spacing.spacing160}`};
  display: flex;
  justify-content: space-between;
`;

export const CopyButton = styled(BaseButton)`
  height: 48px;
  width: 87px;
  ${({ theme }) => css`
    ${theme.typography.buttonLarge};
    background-color: ${theme.colors.surface};
    color: ${theme.colors.primary};
    border: 1px solid ${theme.colors.textOutline};
    margin-right: ${theme.spacing.spacing80};
    padding: ${theme.spacing.spacing60} ${theme.spacing.spacing80};
  `};

  &:not([disabled]):hover {
    background: ${({ theme }) => theme.colors.surface};
  }
`;

export const ActionButton = styled(BaseButton)`
  height: 48px;
  ${({ theme }) => css`
    ${theme.typography.buttonLarge};
    background: ${theme.colors.primary};
  `};

  &[disabled] {
    ${({ theme }) =>
      css`
        color: ${theme.colors.textDisabled};
        background-color: ${theme.colors.stateDisabled};
      `};
    opacity: 1;
  }

  &:not([disabled]):hover {
    background: ${({ theme }) => theme.colors.primary};
  }
`;

export const ActionButtonOffer = styled(UIButton)`
  color: ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borders.radius10};
  width: 100%;
  ${({ theme }) => theme.typography.buttonLarge};
`;

export const ActionButtonCoupon = styled(UIButton)`
  color: ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borders.radius10};
  margin-right: ${({ theme }) => theme.spacing.spacing80};
  ${({ theme }) => theme.typography.buttonLarge};
`;

export const ActionButtonCartCoupon = styled(UIButton)<{ disabled?: boolean }>`
  width: 100%;
  ${({ theme }) => theme.typography.buttonLarge};
  ${({ theme, disabled }) =>
    `border-color: ${disabled && theme.colors.surface30}; background-color: ${
      disabled ? theme.colors.surface30 : theme.colors.primary
    }; border-radius: ${theme.borders.radius10}; color: ${theme.colors.surface}`};
`;

export const DiscountTagWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  ${({ theme }) => `margin-bottom: ${theme.spacing.spacing80}`};
`;

export const CopyButtonPDP = styled(BaseButton)`
  height: 48px;
  flex: 3 1 0;
  ${({ theme }) => theme.typography.buttonLarge};
  background: ${({ theme }) => theme.colors.primary};
`;

export const ActionButtonPDP = styled(BaseButton)`
  flex: 1 1 0;
  ${({ theme }) =>
    css`
      ${theme.typography.buttonLarge};
      border: 1px solid ${theme.colors.textOutline};
      background-color: ${theme.colors.surface};
      color: ${theme.colors.textPrimary};
      margin-right: ${theme.spacing.spacing80};
      padding: calc(${theme.spacing.spacing50}) calc(${theme.spacing.spacing80});
    `};
`;

export const FixedContent = styled.div`
  width: 30%;
  max-width: 400px;
  height: 100%;
  position: fixed;
  right: 0;
  ${({ theme }) => `background: ${theme.colors.surface}`};
  ${({ theme }) => `box-shadow: 0px -${theme.spacing.spacing100} ${
    theme.spacing.spacing100
  } ${hexToRgb(theme.colors.primary, 16)};
    `};
  z-index: 7;
`;

export const Wrapper = styled.div`
  position: relative;
  height: 100%;
  ${({ theme }) => `padding: 0px: ${theme.spacing.spacing240}`};
`;

export const IconContainerDesktop = styled.span`
  ${({ theme }) => `padding: ${theme.spacing.spacing80} ${theme.spacing.spacing160} 0 0`};
  cursor: pointer;
  position: absolute;
  right: 0;
`;

export const OverlaySheetDesktop = styled(BaseOverlaySheet)`
  overflow: hidden;
  ${({ theme }) => `color: ${theme.colors.black100}`};
  border-radius: 0;
  left: 70%;
  width: 30%;
  max-width: 400px;
  overflow: hidden;
`;

export const LockIcon = styled(CircleLock)`
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.secondary10};
  margin-right: ${({ theme }) => theme.spacing.spacing40};
`;

export const CouponImage = styled.div`
  width: 100%;
  background-image: url('https://images-static.nykaa.com/fashion-images/tr:w-720/error404.svg');
`;

export const StickyContainer = styled.div<{ isHidden?: boolean; showBottomBorder?: boolean }>`
  ${({ theme, showBottomBorder }) =>
    showBottomBorder && `border-bottom: 1px solid ${theme.colors.textOutline}`};
  position: sticky;
  top: 0;
  background-color: ${({ theme }) => theme.colors.surface};
  transition: opacity 200ms;

  &.sticky {
    display: ${({ isHidden }) => (isHidden ? 'none' : 'block')};
    width: 100%;
  }
  .sticky-title {
    ${({ theme }) => `padding: ${theme.spacing.spacing80} 0`};
    ${({ theme }) => theme.typography.titleSmall};
  }
`;

export const InfoContainerWrapper = styled.div`
  flex: 1;
`;

export const SaveUptoText = styled.div`
  ${({ theme }) => theme.typography.titleMedium};
  color: ${({ theme }) => theme.colors.positive};
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const LoaderWrapper = styled.div`
  display: flex;
`;

export const UIComponentLoader = styled(Spinner)`
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
`;

export const CouponErrorPage = styled(CouponErrorPageBase)`
  flex: 1;
  align-items: center;
  &.coupon-offer-wrapper {
    margin: ${({ theme }) => `${theme.spacing.spacing320} 0`};
  }
  .coupon-error-title {
    margin-bottom: ${({ theme }) => theme.spacing.spacing40};
  }
`;

export const CouponErrorHeader = styled.div`
  display: flex;
  position: sticky;
  background-color: ${({ theme }) => theme.colors.surface};
  height: 56px;
  top: 0;
  .close-icon {
    position: relative;
  }
`;
